import type {
  CommitDto,
  DatabaseDisposition,
  DeleteApplicationResponseBody,
  GetAllApplicationsResponseBody,
  GetApplicationCommitsResponseBody,
  GetRecentApplicationsResponseBody,
  IHomepageApplicationV2Dto,
  IRecentApplicationDto,
  PutApplicationUpdateResponseBody,
} from "@superblocksteam/shared";

import { reduxApiSlice } from "./index";

/** Home rail limit; keep in sync with `useListRecentApplicationsQuery` callers. */
export const RECENT_APPLICATIONS_HOME_LIMIT = 20;

const extendedApi = reduxApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listApplications: builder.query<IHomepageApplicationV2Dto[], void>({
      query: () => ({
        url: `/v2/applications`,
        method: "GET",
      }),
      transformResponse: (response: { data: GetAllApplicationsResponseBody }) =>
        response.data.applications,
    }),

    listRecentApplications: builder.query<
      IRecentApplicationDto[],
      { limit: number }
    >({
      query: ({ limit }) => ({
        url: `/v2/applications/recent`,
        method: "GET",
        params: { limit },
      }),
      transformResponse: (response: {
        data: GetRecentApplicationsResponseBody;
      }) => response.data.items,
    }),

    updateApplicationMetadata: builder.mutation<
      PutApplicationUpdateResponseBody,
      {
        id: string;
        name?: string;
        folderId?: string | null;
      }
    >({
      query: ({ id, ...updates }) => ({
        url: `/v2/applications/${id}/metadata`,
        method: "PUT",
        body: updates,
      }),
      transformResponse: (response: {
        data: PutApplicationUpdateResponseBody;
      }) => response.data,

      async onQueryStarted({ id, ...updates }, { dispatch, queryFulfilled }) {
        const patches = [
          dispatch(
            extendedApi.util.updateQueryData(
              "listApplications",
              undefined,
              (draft) => {
                const itemIndex = draft.findIndex((item) => item.id === id);
                if (itemIndex !== -1) {
                  draft[itemIndex] = {
                    ...draft[itemIndex],
                    name: updates.name ?? draft[itemIndex].name,
                    folderId:
                      updates.folderId === undefined // specifically test for undefined. A null value is used to indicate the root folder.
                        ? draft[itemIndex].folderId
                        : updates.folderId,
                  };
                }
              },
            ),
          ),
        ];
        if (updates.name !== undefined) {
          const name = updates.name;
          patches.push(
            dispatch(
              extendedApi.util.updateQueryData(
                "listRecentApplications",
                { limit: RECENT_APPLICATIONS_HOME_LIMIT },
                (draft) => {
                  const item = draft.find((entry) => entry.id === id);
                  if (item) {
                    item.name = name;
                  }
                },
              ),
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          for (const patch of patches) {
            patch.undo();
          }
        }
      },
    }),

    deleteApplication: builder.mutation<
      DeleteApplicationResponseBody,
      { databaseDisposition?: DatabaseDisposition; id: string }
    >({
      query: ({ databaseDisposition, id }) => ({
        url: `/v2/applications/${id}`,
        method: "DELETE",
        params: databaseDisposition ? { databaseDisposition } : undefined,
      }),
      transformResponse: (response: { data: DeleteApplicationResponseBody }) =>
        response.data,
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patches = [
          dispatch(
            extendedApi.util.updateQueryData(
              "listApplications",
              undefined,
              (draft) => {
                const index = draft.findIndex((item) => item.id === id);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              },
            ),
          ),
          dispatch(
            extendedApi.util.updateQueryData(
              "listRecentApplications",
              { limit: RECENT_APPLICATIONS_HOME_LIMIT },
              (draft) => {
                const index = draft.findIndex((item) => item.id === id);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              },
            ),
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          for (const patch of patches) {
            patch.undo();
          }
        }
      },
    }),

    getApplicationDatabaseSummary: builder.query<
      {
        applications: Record<string, { hasDatabase: boolean }>;
      },
      { applicationIds: string[] }
    >({
      query: ({ applicationIds }) => {
        const queryParams = new URLSearchParams();
        applicationIds.forEach((id) => queryParams.append("applicationId", id));
        return {
          url: `/v1/database-lifecycle/applications/summary?${queryParams.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: {
        data: {
          applications: Record<string, { hasDatabase: boolean }>;
        };
      }) => response.data,
    }),

    getAppCommits: builder.query<CommitDto[], { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/v2/applications/${applicationId}/commits`,
        method: "GET",
        params: { limit: 10, offset: 0, commitType: "autosave" },
      }),
      transformResponse: (response: {
        data: GetApplicationCommitsResponseBody;
      }) => response.data.autosaves,
    }),

    getScopedToken: builder.query<string, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/v1/token/scoped`,
        method: "POST",
        body: { applicationId, scope: "apps:update" },
      }),
      transformResponse: (response: { access_token: string }) =>
        response.access_token,
      keepUnusedDataFor: 300,
    }),

    getBatchScreenshotMetadata: builder.query<
      Record<string, { commitId: string; token: string }>,
      { applicationIds: string[] }
    >({
      async queryFn({ applicationIds }, _queryApi, _extraOptions, baseQuery) {
        const CHUNK_SIZE = 100;
        const merged: Record<string, { commitId: string; token: string }> = {};

        for (let i = 0; i < applicationIds.length; i += CHUNK_SIZE) {
          const chunk = applicationIds.slice(i, i + CHUNK_SIZE);
          const result = await baseQuery({
            url: `/v2/applications/batch/screenshot-metadata`,
            method: "POST",
            body: { applicationIds: chunk },
          });
          if (result.error) {
            return { error: result.error };
          }
          const payload = result.data as {
            data: {
              screenshots: Record<string, { commitId: string; token: string }>;
            };
          };
          Object.assign(merged, payload.data.screenshots);
        }

        return { data: merged };
      },
      keepUnusedDataFor: 300,
    }),
  }),
});

/** Exported so tests can drive the endpoint without rendering a page. */
export const { listApplications: listApplicationsEndpoint } =
  extendedApi.endpoints;

export const {
  useListApplicationsQuery,
  useListRecentApplicationsQuery,
  useUpdateApplicationMetadataMutation,
  useDeleteApplicationMutation,
  useGetApplicationDatabaseSummaryQuery,

  useGetScopedTokenQuery,
  useGetBatchScreenshotMetadataQuery,
  util: applicationsApiUtil,
} = extendedApi;
